import request from 'shared/lk/network/lkApi'
import * as Analytics from '@/helpers/analytics.js'
import store from '@/store'
import * as Network from './network'
import { assertLogoutUser } from '@/helpers/utils'
import { ErrorFN } from 'shared/network/gateway.types'
import { provideCurrentJWT } from '@/helpers/account'
import { MethodType, ReqConfigModel } from 'shared/lk/network/api.types'
import { captureException } from '@/helpers/sentry'

/**
 * Sends error's response to proper Slack channel.
 * If status is 500 then it's sent to channel '500' otherwise to 'emergency'
 * If error, error's URL or errors's config is not defined, or if error's URL includes 'crisp'
 * then nothing will be sent.
 *
 * @param error
 * @param status Error's status
 * @returns
 */
function handle500Error(error, status) {
    if (!error || !error.config) {
        return
    }
    const { url } = error.config
    if (!url || url.includes('crisp')) {
        return
    }
    status == 500
        ? Analytics.send500Error(error.response, '500')
        : Analytics.send500Error(error.response, 'emergency')
}

/**
 * Handles given error based on it's config, status and type.
 *
 * @param error
 * @param config Error's config
 * @param callback Callback function that is called
 * @returns
 */
const errorFN: ErrorFN<ReqConfigModel> = async (error, config, callback) => {
    if (!error.response) throw error
    const status = error.response.status
    const { type } = error.response.data.error
    switch (true) {
        case error.config.repeated || type == 'AccRemoved':
            return assertLogoutUser()
        case type === 'JWTExpired' || type === 'JWTInavlid':
            return withRefreshJWT(error, callback)
        case status === 500 || status == 502:
            handle500Error(error, status)
            Network.handleError(error, config.errors, config.quiet ?? false)
            throw error
        default:
            Network.handleError(error, config.errors, config.quiet ?? false)
            throw error
    }
}
export const getRequestConfig = (config: object = {}): ReqConfigModel => ({
    ...config,
    jwt: provideCurrentJWT(),
    region: store.getters.getRegion()
})

/**
 * Sends request and gets response from server using URL with school ID provided by Vuex store or router
 *
 * @param method HTTP request method
 * @param url Request URL
 * @param data Request data
 * @param config Request configuration
 * @returns Server's response
 */
const requestWithStore = async (
    method: MethodType,
    url: string,
    data?: any,
    config: ReqConfigModel = {}
) => {
    return request(
        method,
        Network.provideURL(url),
        data,
        getRequestConfig(config),
        errorFN
    )
}

/**
 * Gets JWT from server and commits it to the Vuex store
 *
 * @returns JWT
 */
const refreshJWT = async () => {
    const { data } = await requestWithStore('get', 'v1/refresh-jwt')
    store.commit('REFRESH_JWT_TOKEN', data)
    return data
}

/**
 * Tries to get JWT and reset Websockets, then it returns value returned by callback.
 * If it fails it catches an error and logs user out.
 *
 * @param error
 * @param callback Function that will be executed if
 * @returns The value returned by callback
 */
const withRefreshJWT = async (error, callback) => {
    try {
        const token = await refreshJWT()
        error.config.headers['Authorization'] = `Bearer ${token}`
        error.config.repeated = true
        return callback(error.config)
    } catch (error) {
        return assertLogoutUser()
    }
}
const parseResponse = res => res.data ?? res
// NEW - WILL BE USED IN DECORATED VUEX MODULES
export default {
    get: async (url: string, config?: ReqConfigModel): Promise<any> => {
        return requestWithStore('get', url, {}, config).then(parseResponse)
    },
    post: async (
        url: string,
        payload: any,
        config?: ReqConfigModel
    ): Promise<any> => {
        return requestWithStore('post', url, payload, config).then(
            parseResponse
        )
    },
    put: async (
        url: string,
        payload: any,
        config?: ReqConfigModel
    ): Promise<any> => {
        return requestWithStore('put', url, payload, config).then(parseResponse)
    },
    patch: async (
        url: string,
        payload: any,
        config?: ReqConfigModel
    ): Promise<any> => {
        return requestWithStore('patch', url, payload, config).then(
            parseResponse
        )
    },
    delete: async (
        url: any,
        config?: ReqConfigModel,
        payload: any = {}
    ): Promise<any> => {
        return requestWithStore('delete', url, payload, config).then(
            parseResponse
        )
    }
}

// OLD - FOR BACKWARD COMPABILITY
export function get(url: string, config?: any) {
    return requestWithStore('get', url, {}, config)
}
export function post(url, data) {
    return requestWithStore('post', url, data)
}
export function patch(url, data) {
    return requestWithStore('patch', url, data)
}
export function put(url, data) {
    return requestWithStore('put', url, data)
}
export function remove(url, data = {}) {
    return requestWithStore('delete', url, data)
}
export function handleStoreFetch(
    url = '',
    query = '',
    commit,
    commitPath = ''
) {
    const path = `${url}${query}`
    return requestWithStore('get', path).then(data => {
        if (!query && commitPath && commit) {
            commit(commitPath, data)
        }
        return data
    })
}
